import type { ColumnFilterValue } from './ColumnFilterDropdown';
import './GridFilterBar.css';

/** How many values of one column are spelled out before "… (+n more)". */
const MAX_VALUES_SHOWN = 3;

export interface GridFilterBarProps {
  /** The grid's per-column Excel filters (null = column not filtered). */
  filters: Record<string, ColumnFilterValue>;
  /** Column key → caption, so the bar reads like the header ("[Test]"). */
  labels: Record<string, string>;
  /** Whether the filters are applied. Unticking keeps them but shows every row. */
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  /** Removes every column filter. */
  onClear: () => void;
}

/** One column's filter as XAF's filter panel words it. */
function describe(label: string, sel: Set<string>): string {
  const values = [...sel];
  if (values.length === 0) return `[${label}] Is none of the values`;
  const quoted = values.map(v => `'${v === '' ? '(Blanks)' : v}'`);
  if (quoted.length === 1) return `[${label}] Equals ${quoted[0]}`;
  const shown = quoted.slice(0, MAX_VALUES_SHOWN).join(', ');
  const more = quoted.length > MAX_VALUES_SHOWN ? `, … (+${quoted.length - MAX_VALUES_SHOWN} more)` : '';
  return `[${label}] In (${shown}${more})`;
}

/**
 * The active-filter strip shown under a filtered grid — the ALPACA (XAF) filter
 * panel: the filter written out as an expression, a tick box to switch it off and
 * on without losing it, and Clear. Renders nothing when no column is filtered, so
 * its presence alone tells the user the grid is not showing every row.
 */
export function GridFilterBar({ filters, labels, enabled, onToggle, onClear }: GridFilterBarProps) {
  const parts = Object.entries(filters)
    .filter((e): e is [string, Set<string>] => e[1] != null)
    .map(([key, sel]) => describe(labels[key] ?? key, sel));
  if (parts.length === 0) return null;
  const text = parts.join(' And ');

  return (
    <div className={`gfb${enabled ? '' : ' gfb--off'}`} role="status">
      <input type="checkbox" checked={enabled}
             onChange={e => onToggle(e.target.checked)}
             title={enabled ? 'Turn the filter off (keeps it)' : 'Turn the filter back on'}
             aria-label="Apply filter" />
      <i className="ti ti-filter" aria-hidden />
      <span className="gfb-text" title={text}>{text}</span>
      <button type="button" className="gfb-clear" onClick={onClear}>Clear</button>
    </div>
  );
}
